import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Menu, ShoppingCart, User, LogOut, Heart, Package, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { useCart, useUI } from "../lib/cart-context";
import { useAuth } from "../lib/auth-context";
import { useShop, useShopHref } from "../lib/shop-context";

export default function Header() {
  const { itemCount } = useCart();
  const { setCartOpen, setCategoryDrawerOpen, categoryDrawerOpen } = useUI();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const shopCtx = useShop();
  const shopHref = useShopHref();
  const [q, setQ] = React.useState("");

  // Phase 30 — smooth scroll to top when the user taps the primary nav
  // actions (Carrito / Categorías / Perfil). Keeps the header sticky on
  // mobile so tapping "Carrito" first surfaces the page top, then the
  // drawer opens over it.
  const scrollTop = () => {
    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      window.scrollTo(0, 0);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    const query = q.trim();
    if (query.length > 0) navigate(`${shopHref("/buscar")}?q=${encodeURIComponent(query)}`);
  };

  const doLogout = () => {
    logout();
    toast.success("Sesión cerrada");
    navigate("/", { replace: true });
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-30 w-full bg-white border-b border-neutral-200"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center gap-3">
        <Button
          data-testid="open-category-drawer-btn"
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={() => {
            scrollTop();
            setCategoryDrawerOpen((v) => !v);
          }}
          aria-expanded={categoryDrawerOpen}
          aria-controls="mega-categorias"
          aria-label="Categorías"
        >
          <Menu className="h-5 w-5" />
        </Button>

        <Link
          to={shopCtx ? shopCtx.prefix + "/" : "/"}
          data-testid="header-logo"
          className="flex items-center gap-2 shrink-0"
        >
          <div className="h-9 w-9 rounded-xl bg-[#00953B] text-white grid place-items-center font-black">
            M
          </div>
          <span className="text-xl font-black tracking-tight">
            Merca<span className="text-[#00953B]">Go</span>
          </span>
        </Link>

        {/* Phase 35 — the storefront toggle was removed. `local` is now
            hidden from the UI; all traffic flows through `envio`. Legacy
            `/local/*` URLs are redirected in App.js. */}

        <Button
          data-testid="open-category-drawer-desktop"
          variant="outline"
          className="hidden lg:inline-flex ml-2 border-neutral-200 gap-2 rounded-full"
          onClick={() => {
            scrollTop();
            setCategoryDrawerOpen((v) => !v);
          }}
          aria-expanded={categoryDrawerOpen}
          aria-controls="mega-categorias"
        >
          <Menu className="h-4 w-4" /> Categorías
        </Button>

        <form
          onSubmit={submit}
          data-testid="header-search-form"
          className="hidden md:flex flex-1 max-w-2xl mx-4"
        >
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <Input
              data-testid="header-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar productos, marcas y más…"
              className="pl-9 h-11 rounded-full bg-neutral-50 border-neutral-200 focus-visible:ring-[#00953B]/40"
            />
          </div>
        </form>

        <div className="ml-auto flex items-center gap-1 sm:gap-2">
          {user ? (
            <>
              {user.role === "admin" && (
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="hidden md:inline-flex border-[#00953B] text-[#00953B] rounded-full"
                  data-testid="header-admin-badge"
                >
                  <Link to="/admin">Admin</Link>
                </Button>
              )}
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  data-testid="header-user-menu-btn"
                  onClick={scrollTop}
                  className="gap-2 rounded-full"
                >
                  <div className="h-7 w-7 rounded-full bg-[#F1FAF3] text-[#00953B] grid place-items-center font-bold text-sm">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <span className="hidden sm:inline max-w-[120px] truncate">
                    {user.name.split(" ")[0]}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="user-menu-account">
                  <Link to="/mi-cuenta" className="gap-2">
                    <User className="h-4 w-4" /> Mi cuenta
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="user-menu-orders">
                  <Link to="/mis-pedidos" className="gap-2">
                    <Package className="h-4 w-4" /> Mis pedidos
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="user-menu-shipments">
                  <Link to="/mis-envios" className="gap-2">
                    <Send className="h-4 w-4" /> Mis envíos
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="user-menu-favorites">
                  <Link to="/mis-habituales" className="gap-2">
                    <Heart className="h-4 w-4" /> Mis habituales
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={doLogout}
                  data-testid="user-menu-logout"
                  className="gap-2 text-red-600 focus:text-red-600"
                >
                  <LogOut className="h-4 w-4" /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </>
          ) : (
            <Button
              asChild
              variant="ghost"
              className="gap-2"
              aria-label="Iniciar sesión"
              onClick={scrollTop}
            >
              <Link to="/entrar" data-testid="header-login-btn">
                <User className="h-5 w-5" />
                <span className="hidden sm:inline">Iniciar sesión</span>
              </Link>
            </Button>
          )}

          <Button
            data-testid="open-cart-btn"
            variant="outline"
            className="relative rounded-full border-neutral-200 gap-2 pl-3 pr-4"
            onClick={() => {
              scrollTop();
              setCartOpen(true);
            }}
          >
            <ShoppingCart className="h-4 w-4" />
            <span className="hidden sm:inline">Carro</span>
            {itemCount > 0 && (
              <Badge
                data-testid="cart-count-badge"
                className="ml-1 bg-[#00953B] hover:bg-[#00953B] text-white h-5 px-2 rounded-full"
              >
                {itemCount}
              </Badge>
            )}
          </Button>
        </div>
      </div>

      <form
        onSubmit={submit}
        data-testid="mobile-search-form"
        className="md:hidden px-4 pb-3"
      >
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <Input
            data-testid="mobile-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar productos…"
            className="pl-9 h-11 rounded-full bg-neutral-50 border-neutral-200"
          />
        </div>
      </form>
    </header>
  );
}
